import React, { ComponentPropsWithRef, useMemo } from 'react';
import { useListBox } from 'react-aria';
import { genericForwardRef, genericMemo } from '@direct-frontend/stdlib';
import { useListState } from 'react-stately';
import { isMobile } from '@direct-frontend/platform-code';
import { useLogger } from '@direct-frontend/modules-logger';

import { ListBoxBase } from '../ListBoxBase/ListBoxBase';
import { Item, ListAddonContext } from '../ListBoxBase/Item/Item';
import { Item as ItemType } from '../ListBoxBase/Item/types';
import { getItemKey } from '../ListBoxBase/Item/utils';
import { cnListBox } from '../ListBoxBase/utils';
import { ListSection } from '../List/ListSection';
import { ListContext } from '../List/utils';

import { ListBoxPropsRaw } from './types';
import { processItems, useListBoxContent } from './utils';

/**
 * Компонент контейнер для построения селектов и других списков с выбором элементов
 *
 * @deprecated Используейте List с ListItem вместо данного компонента
 */
export const ListBox = genericMemo(
    genericForwardRef(function ListBox<ValueType extends string, SectionType extends string = ValueType>(
        props: ListBoxPropsRaw<ValueType, SectionType, ItemType<ValueType, SectionType>>,
        forwardedRef: React.ForwardedRef<HTMLDivElement>,
    ) {
        const {
            addonBefore,
            addonAfter,
            className,
            onScroll,
            size,
            width = 'auto',
            style,
            displaySelection = true,
            state,
            'data-testid': dataTestId = 'Listbox',
            logNode,
            preventOverscroll,
        } = props;
        const { ref, listProps, scrollClass, hasIconLeft } = useListBoxContent(
            {
                ...props,
                items: processItems({ items: props.items, shouldAddAddon: Boolean(props.addonBeforeSection) }),
            },
            forwardedRef,
        );
        const { LogProvider } = useLogger('ListBox', logNode);
        const defaultState = useListState(listProps);
        const listBoxState = state ?? defaultState;
        const { listBoxProps } = useListBox(listProps, listBoxState, ref);
        const items = Array.from(listBoxState.collection).map(node =>
            node.value ? (
                <Item key={getItemKey(node.value)} node={node} state={listBoxState} baseTestId={dataTestId} />
            ) : null,
        );

        const listSize = isMobile() ? 'm' : size;
        const contextValue = useMemo(
            () => ({
                size: listSize,
            }),
            [listSize],
        );

        return (
            <LogProvider>
                <ListContext.Provider value={contextValue}>
                    <ListAddonContext.Provider value={{ listAddon: props.addonBeforeSection }}>
                        <ListBoxBase
                            size={isMobile() ? 'm' : size}
                            width={width}
                            style={style}
                            displaySelection={displaySelection}
                            className={cnListBox({ 'has-icon-left': hasIconLeft }, [className])}
                        >
                            {addonBefore}
                            {props.pinnedSection ? (
                                <>
                                    <ListSection>{props.pinnedSection}</ListSection>
                                    {items.length !== 0 && <ListSection />}
                                </>
                            ) : null}
                            <div
                                {...listBoxProps}
                                className={cnListBox('list', { 'prevent-overscroll': preventOverscroll }, [
                                    scrollClass,
                                ])}
                                onScroll={onScroll}
                                ref={ref}
                                data-testid={dataTestId}
                            >
                                {items}
                            </div>
                            {props.pinnedSectionAfter ? (
                                <>
                                    {items.length !== 0 && !props.pinnedSection && <ListSection />}
                                    <ListSection>{props.pinnedSectionAfter}</ListSection>
                                </>
                            ) : null}
                            {addonAfter}
                        </ListBoxBase>
                    </ListAddonContext.Provider>
                </ListContext.Provider>
            </LogProvider>
        );
    }),
);

export type ListBoxProps = ComponentPropsWithRef<typeof ListBox>;
